// TODO: cleanup with research_assistant_gen_3 ff
//
// Project-scoped chat panel — the shared chat surface mounted by gen_3's
// in-page split layout (left pane of {@link ProjectPage}).
//
// One panel, two states:
//   - ``chatId`` absent  → {@link ChatPanelEmptyState} (composer + Your chats / Activity tabs)
//   - ``chatId`` present → {@link ActiveChatBody} (header + EpiChatPanelLayout)
//
// {@link MaybeActiveChatProvider} mounts a single ``useEpiChatPage`` per
// ``chatId`` so the right-pane draft surface (gen_3 only today) can read
// the same chat stream without double-subscribing on the socket.

import { Box } from '@mui/material';
import { useEffect, useMemo, useRef, type ReactNode } from 'react';
import { useIntl } from 'react-intl';

import { useFeatureFlagSplit } from '@verantos-dev/app-state-base';
import {
  selectIsExecutionPending,
  useCohortDefinitionStore,
} from '@verantos-dev/app-state-cohort-definition';
import { getProjectVariableGroupPath } from '@verantos-dev/core-constants';
import type { EpiAttachment, EpiMessage, EpiMessagePart } from '@verantos-dev/feature-epi-chat';
import { EpiChatPanelLayout, parseEchoedAttachments } from '@verantos-dev/feature-epi-chat';

import { ActiveChatHeader } from './ActiveChatHeader';
import { ChatPanelEmptyState } from './ChatPanelEmptyState';
import { ChatPanelTitle } from './ChatPanelTitle';
import { ReadOnlyChatPanel } from './ReadOnlyChatPanel';
import { ActiveChatProvider, useActiveChatContext } from '../context/ActiveChatContext';
import { useChatResourceNavigation } from '../hooks/useChatResourceNavigation';
import { useInternalChatVisibilityEnabled } from '../hooks/useInternalChatVisibilityEnabled';
import { useWelcomeVariant } from '../hooks/useWelcomeVariant';
import { useActiveChat } from '../lib/api/useProjectChats';

/**
 * Parse the empty-state composer's initial-message stash (see
 * ``ChatPanelEmptyState``). Current format is `{id, text, attachments}`; a bare
 * string is treated as legacy/malformed and falls back to text-only (the real
 * send mints its own id in that case, same as before the id-reuse fix).
 *
 * ``attachments`` is validated rather than trusted: it reaches ``.map`` during
 * render, so a malformed value would blank the pane instead of losing a card.
 */
interface InitialStash {
  id?: string;
  text: string;
  attachments?: EpiAttachment[];
}

const parseInitialStash = (raw: string): InitialStash => {
  try {
    const parsed: unknown = JSON.parse(raw);
    const hasTextField =
      typeof parsed === 'object' &&
      parsed !== null &&
      typeof (parsed as { text?: unknown }).text === 'string';
    if (!hasTextField) return { text: raw };
    const stash = parsed as { id?: unknown; text: string; attachments?: unknown };
    return {
      id: typeof stash.id === 'string' ? stash.id : undefined,
      text: stash.text,
      attachments: parseEchoedAttachments(stash.attachments),
    };
  } catch {
    return { text: raw };
  }
};

interface ProjectChatPanelProps {
  projectId: string;
  /**
   * Per-chat session id from the URL's ``?chat=<id>`` query param. When
   * set, this is the active chat thread; the panel renders the
   * familiar `EpiChatPanelLayout`. When undefined, the panel renders
   * `ChatPanelEmptyState` — chats list + new-chat composer.
   *
   * Plumbed into ``useEpiChatPage`` as both the FE state slot anchor AND
   * the wire-side `chat_id` on `subscribe`.
   */
  chatId: string | undefined;
}

interface ActiveChatBodyProps extends ProjectChatPanelProps {
  chatId: string;
}

const ActiveChatBody = ({ projectId, chatId }: ActiveChatBodyProps) => {
  const intl = useIntl();
  const ctx = useActiveChatContext();
  // Provider invariant: ``ActiveChatBody`` is only rendered inside
  // ``ActiveChatProvider`` (both mounts are gated on the same ``chatId``).
  if (!ctx) {
    throw new Error('ActiveChatBody rendered outside ActiveChatProvider');
  }
  const { page, composerPlaceholder } = ctx;
  // OR-6021: no scope chip is rendered. The BE still emits
  // ``task_scope`` events into ``page.remoteFocusScope`` (carried by
  // the slice for observability + future surfaces) and the
  // ``useTaskScopeChip`` hook in feature-epi-chat is wired and ready
  // for callers that want to mount the chip later — but the
  // task-scope-driven chip didn't feel great in practice (toggled
  // visibly between draft and persisted variants; persisted emits
  // don't carry titles on the wire so the name flickered). The chat
  // already communicates what the agent is working on via artifact
  // cards and tool-call output.
  const scopeChip = null;
  const { navigate, onArtifactOpen, onAvSlugClick, onConceptGroupNavigate } =
    useChatResourceNavigation(projectId);

  // TODO: cleanup with skip_process_confirmation_modal ff
  // isExecutionPending is set for both the modal-confirm and inline process
  // flows (shared in useCohortBuilderSaveQuery)
  const isInlineProcessEnabled = Boolean(useFeatureFlagSplit('skip_process_confirmation_modal'));
  const isCohortQueuing = useCohortDefinitionStore(selectIsExecutionPending);
  const composerBlockedMessage =
    isInlineProcessEnabled && isCohortQueuing
      ? intl.formatMessage({ id: 'projectPage.chatPanel.composerBlocked.cohortQueuing' })
      : null;

  // A chat's row must exist server-side before its first message runs: the
  // run acquires a lease keyed on that row and the server does not create one
  // on demand. The chats-list cache holds an optimistic placeholder (empty
  // ownerUserId) until the server-confirmed row replaces it, so a non-empty
  // ownerUserId means the chat is committed. Reading it from the cache (not a
  // local flag) keeps the signal correct across a reload.
  const { chat: confirmedChat } = useActiveChat({ projectId, chatId });
  const createConfirmed = Boolean(confirmedChat?.ownerUserId);

  // OR-6123 (Batch A): a zero-message chat shows the static welcome
  // greeting. ``EpiChatPanelLayout`` only renders the greeting when the
  // message list is empty, so passing the variant unconditionally is safe
  // — once a message exists the empty-state branch never shows. Held until
  // the chats list resolves so the variant doesn't flicker.
  const { variant: welcomeVariant, isReady: welcomeReady } = useWelcomeVariant(projectId, chatId);

  // Flush an initial message stashed by the empty-state composer once
  // the chat is BE-confirmed and the socket subscribe ACKs. See
  // ChatPanelEmptyState for the stash side; the drain logic here waits on
  // (1) slot bind, (2) create confirmed, (3) subscribe start, (4) subscribe
  // ACK, (5) idle runState before firing exactly once per chatId.
  const initialFlushedRef = useRef<string | null>(null);
  const subscribingSeenTrueRef = useRef(false);
  const { activeSessionId, runState, subscribing, sendMessage } = page;

  useEffect(() => {
    if (subscribing) {
      subscribingSeenTrueRef.current = true;
    }
  }, [subscribing]);

  // Read the empty-state composer's stash once per chatId (not on every
  // render) so its identity is stable for the optimistic-bubble render below
  // — re-reading after ``sessionStorage.removeItem`` (once the real send
  // fires) would otherwise flip this back to ``null`` for a render or two
  // before ``messages`` catches up, reintroducing the very flash this exists
  // to prevent.
  const pendingInitialMessage = useMemo<InitialStash | null>(() => {
    try {
      const raw = sessionStorage.getItem(`epi-chat-initial:${chatId}`);
      return raw ? parseInitialStash(raw) : null;
    } catch {
      return null;
    }
  }, [chatId]);

  // Renders the researcher's own first message immediately — before the
  // socket even connects — instead of the "Loading session…" spinner
  // (OR-6123 gap: the spinner could otherwise show for several seconds while
  // the chat row is created and the socket subscribes). Reuses the same
  // client-minted id as the eventual real send below, and MessageList only
  // renders this while ``messages`` is empty, so it steps aside the instant
  // the real message lands.
  const pendingOptimisticMessage = useMemo<EpiMessage | null>(() => {
    if (!pendingInitialMessage) return null;
    return {
      id: pendingInitialMessage.id ?? `pending-${chatId}`,
      role: 'user',
      // Cards lead, as the reducer builds a sent turn, so this matches the real one.
      parts: [
        ...(pendingInitialMessage.attachments ?? []).map(
          (attachment): EpiMessagePart => ({ kind: 'attachment', attachment })
        ),
        { kind: 'text', text: pendingInitialMessage.text },
      ],
      status: 'complete',
      timestamp: new Date().toISOString(),
    };
  }, [pendingInitialMessage, chatId]);

  // OR-6348: true from the instant a fresh chat has a message waiting to be
  // flushed until the drain effect below actually fires the real send — the
  // chat-row-create + subscribe round-trip has no real run yet to key the
  // working indicator off, so without this the user sees their own bubble
  // and nothing else for however long that takes. Derived straight off the
  // drain effect's own "already flushed" bookkeeping so it can't drift out
  // of sync with it.
  const awaitingInitialSend =
    Boolean(pendingInitialMessage) && initialFlushedRef.current !== chatId;

  useEffect(() => {
    if (initialFlushedRef.current === chatId) return;
    const pending = pendingInitialMessage;
    if (!pending) {
      initialFlushedRef.current = chatId;
      return;
    }
    if (activeSessionId !== chatId) {
      // eslint-disable-next-line no-console
      console.debug('[ChatPanel] initial-message drain: waiting for slot bind', {
        chatId,
        activeSessionId,
      });
      return;
    }
    if (!createConfirmed) {
      // The chat row isn't confirmed server-side yet; sending now would run
      // before the row exists and the lease acquire would fail.
      // eslint-disable-next-line no-console
      console.debug('[ChatPanel] initial-message drain: waiting for create to confirm', {
        chatId,
      });
      return;
    }
    if (!subscribingSeenTrueRef.current) {
      // eslint-disable-next-line no-console
      console.debug('[ChatPanel] initial-message drain: waiting for subscribe to start');
      return;
    }
    if (subscribing) {
      // eslint-disable-next-line no-console
      console.debug('[ChatPanel] initial-message drain: waiting for subscribe ACK', {
        chatId,
      });
      return;
    }
    if (runState !== 'idle') {
      // eslint-disable-next-line no-console
      console.debug('[ChatPanel] initial-message drain deferred — runState busy', {
        chatId,
        runState,
      });
      return;
    }
    initialFlushedRef.current = chatId;
    try {
      sessionStorage.removeItem(`epi-chat-initial:${chatId}`);
    } catch {
      /* ignore */
    }
    // eslint-disable-next-line no-console
    console.debug('[ChatPanel] flushing initial message', {
      chatId,
      activeSessionId,
      runState,
      length: pending.text.length,
    });
    sendMessage({
      text: pending.text,
      id: pending.id,
      ...(pending.attachments?.length ? { attachments: pending.attachments } : {}),
    });
  }, [
    chatId,
    activeSessionId,
    createConfirmed,
    runState,
    subscribing,
    sendMessage,
    pendingInitialMessage,
  ]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        minHeight: 0,
        minWidth: 0,
      }}
    >
      <ChatPanelTitle />
      <ActiveChatHeader projectId={projectId} chatId={chatId} />
      <Box sx={{ flex: 1, minHeight: 0, minWidth: 0, display: 'flex' }}>
        <EpiChatPanelLayout
          page={page}
          scopeChip={scopeChip}
          composerPlaceholder={composerPlaceholder}
          onArtifactOpen={onArtifactOpen}
          onAvSlugClick={onAvSlugClick}
          // OR-6089: resource_ref card clicks route via the same
          // chat-preserving navigate as the artifact list. Routes are
          // BE-computed (kind-aware) so this side just hands the path
          // through; preserves `?chat=` so the conversation survives
          // the navigation.
          onResourceNavigate={navigate}
          // OR-6504: a variable GROUP has no asset id to route by — nothing is
          // created until the researcher approves — so its card routes by
          // family id to a page that renders from the replayed checkpoint.
          onOpenVariableGroup={(familyId) =>
            navigate(getProjectVariableGroupPath(projectId, familyId))
          }
          // OR-6126: a concept-group link in chat routes to the in-pane
          // concept-group artifact (project context → breadcrumb back) instead
          // of opening the details modal.
          onConceptGroupNavigate={onConceptGroupNavigate}
          attachmentUploadProjectId={projectId}
          welcomeVariant={welcomeReady ? welcomeVariant : undefined}
          pendingOptimisticMessage={pendingOptimisticMessage}
          forceWorkingIndicator={awaitingInitialSend}
          composerBlockedMessage={composerBlockedMessage}
          data-testid="project-page-chat-panel"
        />
      </Box>
    </Box>
  );
};

/**
 * The project-scoped chat panel.
 *
 * - No ``chatId`` → {@link ChatPanelEmptyState} (composer + tabs).
 * - ``chatId`` present, not the caller's own or a team-shared chat, and
 *   only resolvable via the OR-6455 internal-visibility partition →
 *   {@link ReadOnlyChatPanel} — a one-shot snapshot render, never a live
 *   subscribe. See {@link MaybeActiveChatProvider}, which independently
 *   makes this same call before deciding whether to mount the live
 *   ``useEpiChatPage`` provider at all.
 * - Otherwise → {@link ActiveChatBody}. The caller is responsible for
 *   wrapping this with {@link MaybeActiveChatProvider} (or an equivalent
 *   ``ActiveChatProvider``) so the ``useEpiChatPage`` mount is shared with
 *   any sibling panes that need to read the same stream.
 */
export const ProjectChatPanel = ({ projectId, chatId }: ProjectChatPanelProps) => {
  // ``chatId`` is resolved by the caller via {@link useEffectiveChatId}
  // so URL → sessionStorage fallback is synchronous; the panel itself
  // just renders the appropriate state.
  const internalVisibilityEnabled = useInternalChatVisibilityEnabled();
  const { chat, viaInternalVisibility, isLoading } = useActiveChat({
    projectId,
    chatId,
    internalVisibilityEnabled,
  });

  if (!chatId) {
    // Welcome/empty state still shows the chat header + switcher so the user
    // can switch chats or start a new one. (F6 removed the old empty-state
    // chats list; without this header the welcome shell has no switch
    // affordance at all.)
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          width: '100%',
          minHeight: 0,
          minWidth: 0,
        }}
      >
        <ChatPanelTitle />
        <ActiveChatHeader projectId={projectId} chatId={undefined} />
        <Box sx={{ flex: 1, minHeight: 0, minWidth: 0, display: 'flex' }}>
          <ChatPanelEmptyState projectId={projectId} />
        </Box>
      </Box>
    );
  }
  if (viaInternalVisibility) {
    return (
      <ReadOnlyChatPanel
        projectId={projectId}
        chatId={chatId}
        ownerUserId={chat?.ownerUserId}
        ownerDisplayName={chat?.ownerDisplayName ?? undefined}
      />
    );
  }
  // Only an internal-visibility-enabled caller can ever have
  // `viaInternalVisibility` resolve `true`, so only THAT caller needs to
  // wait on `isLoading` before it's safe to mount the live, writable panel
  // — for every other caller (the overwhelming majority), `all` is never
  // queried at all and `viaInternalVisibility` can never flip true, so
  // gating on `isLoading` here would only add a needless blank-panel flash
  // to every normal chat open for no safety benefit.
  if (internalVisibilityEnabled && isLoading) {
    return null;
  }
  return <ActiveChatBody projectId={projectId} chatId={chatId} />;
};

interface MaybeActiveChatProviderProps {
  projectId: string;
  chatId: string | undefined;
  children: ReactNode;
}

/**
 * Mount {@link ActiveChatProvider} — the live socket subscribe — only when
 * a chat is open AND it's the caller's own or a team-shared chat.
 *
 * OR-6455: a chat only resolvable via the internal-visibility partition
 * must NEVER reach the live namespace, structurally, not just via
 * {@link ProjectChatPanel}'s own render branch — this provider is mounted
 * as a SIBLING of the panel (see ``ProjectPage``), so without this same
 * check here, opening someone else's chat through the "All" picker would
 * still fire a live ``subscribe`` from this provider even though the panel
 * itself renders the read-only view. Both checks read the same cached
 * ``useActiveChat`` partitions, so they can't disagree.
 */
export const MaybeActiveChatProvider = ({
  projectId,
  chatId,
  children,
}: MaybeActiveChatProviderProps) => {
  const internalVisibilityEnabled = useInternalChatVisibilityEnabled();
  const { viaInternalVisibility, isLoading } = useActiveChat({
    projectId,
    chatId,
    internalVisibilityEnabled,
  });
  if (!chatId) return <>{children}</>;
  // Hold off subscribing until ownership is resolved — same guard as
  // ProjectChatPanel's render branch, so the two never briefly disagree.
  // Scoped to internal-visibility-enabled callers only (see that branch's
  // comment) so a normal chat open never waits on this at all.
  if ((internalVisibilityEnabled && isLoading) || viaInternalVisibility) return <>{children}</>;
  return (
    <ActiveChatProvider projectId={projectId} chatId={chatId}>
      {children}
    </ActiveChatProvider>
  );
};
